<script setup lang="ts">
  import AddressComponent from '@/components/AddressComponent.vue'
  import { useAddressBookStore } from '@/store/address-book.store'
  import {
    isUuid,
    FormInput,
    DropDown,
    AddressBookOrganizationType,
    AddressBookLocationType,
    UiButton,
    Table,
    Modal,
    type AddressBookOrganization,
    deepEqual,
    type AddressBookLocation,
    type Address,
    ConfirmationDialog,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref, toRaw, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import { useServerValidation } from '@/composables/validation/use-server-validation'
  import { isAxiosError } from 'axios'
  import { formatAddressErrors } from '@/utils/server-error'
  import type { UUID } from 'node:crypto'
  import {
    createEmptyAddress,
    createEmptyAddressBookLocation,
    normalizeEditedLocation,
    normalizeLocation,
    type CreateAddressBookLocation,
  } from '@/utils/address-utils'
  import { createFlatBaseColumns } from '@/utils/table'
  import TopBar from '@/components/TopBar.vue'

  const route = useRoute()
  const addressBookStore = useAddressBookStore()
  const toastStore = useToastStore()

  const organisationId = ref<UUID | null>(null)
  const locationToDelete = ref<AddressBookLocation | null>(null)

  type TypeOption<T extends string> = { value: T; label: string }

  function createTypeOptions<T extends string>(enumObject: Record<string, T>): TypeOption<T>[] {
    return Object.values(enumObject).map(value => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1),
    }))
  }

  type OrganizationTypeOption = TypeOption<AddressBookOrganizationType>
  const organizationTypeOptions: OrganizationTypeOption[] = createTypeOptions(
    AddressBookOrganizationType
  )

  const localAddressBookOrganisation = ref<Partial<AddressBookOrganization> | null>(null)
  const originalAddressBookOrganisation = ref<Partial<AddressBookOrganization> | null>(null)

  const validation = useServerValidation()
  const addressValidation = validation.scope('address', formatAddressErrors)

  addressValidation.clearOnChange(() => localAddressBookOrganisation.value?.address)

  function emptyToNull(value?: string | null): string | null {
    const trimmed = value?.trim()
    return trimmed ? trimmed : null
  }

  function normalizeOrganisation(
    organisation: Partial<AddressBookOrganization>
  ): Partial<AddressBookOrganization> {
    if (!organisation.address) return organisation

    return {
      ...organisation,
      address: {
        ...organisation.address,
        suffix: emptyToNull(organisation.address.suffix),
        street2: emptyToNull(organisation.address.street2),
      },
    }
  }

  const hasChanges = computed(
    () =>
      !!localAddressBookOrganisation.value &&
      !!originalAddressBookOrganisation.value &&
      !deepEqual(localAddressBookOrganisation.value, originalAddressBookOrganisation.value)
  )

  function snapshot() {
    const source = toRaw(addressBookStore.addressBookOrganisation)
    localAddressBookOrganisation.value = structuredClone(source)
    originalAddressBookOrganisation.value = structuredClone(source)
  }

  const selectedTypes = computed<OrganizationTypeOption[]>({
    get: () =>
      organizationTypeOptions.filter(option =>
        localAddressBookOrganisation.value?.types?.includes(option.value)
      ),
    set: items => {
      if (!localAddressBookOrganisation.value) return
      const selected = new Set(items.map(item => item.value))
      localAddressBookOrganisation.value.types = organizationTypeOptions
        .filter(option => selected.has(option.value))
        .map(option => option.value)
    },
  })

  async function saveChanges() {
    if (!localAddressBookOrganisation.value) return

    const normalized = normalizeOrganisation(toRaw(localAddressBookOrganisation.value))

    try {
      await addressBookStore.updateAddressBookOrganization(normalized)

      localAddressBookOrganisation.value = structuredClone(normalized)
      originalAddressBookOrganisation.value = structuredClone(normalized)

      toastStore.success('Organisatie succesvol geupdate')
    } catch (error) {
      const handled =
        isAxiosError(error) &&
        error.response?.status === 400 &&
        validation.setFromResponse(error.response.data)
      if (!handled) throw error
    }
  }

  type LocationTypeOption = TypeOption<AddressBookLocationType>
  const locationTypeOptions: LocationTypeOption[] = createTypeOptions(AddressBookLocationType)

  const showAddressBookLocationModal = ref(false)
  const newLocation = ref<CreateAddressBookLocation>(createEmptyAddressBookLocation())

  const locationValidation = useServerValidation()
  const locationAddressValidation = locationValidation.scope('address', formatAddressErrors)

  locationAddressValidation.clearOnChange(() => newLocation.value.address)

  function closeLocationModal() {
    showAddressBookLocationModal.value = false
  }

  const selectedLocationTypes = computed<LocationTypeOption[]>({
    get: () => locationTypeOptions.filter(option => newLocation.value.types.includes(option.value)),
    set: items => {
      const selected = new Set(items.map(item => item.value))
      newLocation.value.types = locationTypeOptions
        .filter(option => selected.has(option.value))
        .map(option => option.value)
    },
  })

  function toggleLocationType(item: LocationTypeOption) {
    const isSelected = selectedLocationTypes.value.some(type => type.value === item.value)
    selectedLocationTypes.value = isSelected
      ? selectedLocationTypes.value.filter(type => type.value !== item.value)
      : [...selectedLocationTypes.value, item]
  }

  const originalNewLocation = ref<CreateAddressBookLocation | null>(null)

  function openLocationModal() {
    const fresh: CreateAddressBookLocation = {
      ...createEmptyAddressBookLocation(),
      types: [AddressBookLocationType.PROCESSOR],
      address: createEmptyAddress(),
    }

    newLocation.value = fresh
    originalNewLocation.value = structuredClone(toRaw(fresh))
    showAddressBookLocationModal.value = true
  }

  const canSaveLocation = computed(
    () => !!originalNewLocation.value && !deepEqual(newLocation.value, originalNewLocation.value)
  )

  async function saveNewLocation() {
    if (!organisationId.value) return

    const payload = normalizeLocation(toRaw(newLocation.value))

    try {
      await addressBookStore.createAddressBookLocation(organisationId.value, payload)
      await addressBookStore.fetchAddressBookLocations(organisationId.value)

      closeLocationModal()
      toastStore.success('Locatie succesvol toegevoegd')
    } catch (error) {
      const handled =
        isAxiosError(error) &&
        error.response?.status === 400 &&
        locationValidation.setFromResponse(error.response.data)
      if (!handled) throw error
    }
  }

  onMounted(async () => {
    const id = route.params['id']
    if (!isUuid(id)) throw Error(`${id} is not a valid UUID format`)
    organisationId.value = id

    await addressBookStore.fetchAddressBook(id)
    await addressBookStore.fetchAddressBookLocations(id)

    snapshot()
  })

  const placeholderLocation: AddressBookLocation = {
    id: crypto.randomUUID(),
    organizationId: crypto.randomUUID(),
    types: [],
    addressId: null,
    locationNumber: null,
    address: { ...createEmptyAddress(), id: crypto.randomUUID(), customerId: null },
  }

  const allLocationKeys: (keyof AddressBookLocation)[] = [
    'id',
    'organizationId',
    'types',
    'addressId',
    'locationNumber',
    'address',
  ]

  const selectedLocationKeys: (keyof AddressBookLocation)[] = [
    'id',
    'organizationId',
    'types',
    'addressId',
    'locationNumber',
    'address',
  ]

  const locationColumns = computed(() => {
    const rows = addressBookStore.addressBookLocations
    const hasData = rows.length > 0

    const result = createFlatBaseColumns(
      hasData ? rows : [placeholderLocation],
      hasData ? ['deletedAt', 'createdAt', 'updatedAt', 'customPropertyGroupId'] : [],
      {
        searchableKeys: hasData ? selectedLocationKeys : allLocationKeys,
        columnOrder: hasData ? selectedLocationKeys : allLocationKeys,
      }
    )

    if (hasData) result.push({ key: 'action', label: 'actie' })
    return result
  })

  function formatAddressForTable(address?: Address | null) {
    if (!address) return ''
    let result = `${address.street} ${address.houseNumber}`
    if (address.suffix) result += address.suffix
    result += `, ${address.postalCode} ${address.city}`
    if (address.country) result += `, ${address.country}`
    return result
  }

  watch(
    () => newLocation.value.locationNumber,
    () => locationValidation.clear('locationNumber')
  )

  watch(
    () => localAddressBookOrganisation.value?.name,
    () => locationValidation.clear('name')
  )

  function openDeleteDialog(location: AddressBookLocation) {
    locationToDelete.value = location
  }

  async function deleteLocation(locationId: UUID) {
    const organizationId = route.params['id']
    if (!isUuid(organizationId)) throw Error(`${organizationId} is not a valid UUID format`)

    try {
      await addressBookStore.deleteAddressBookLocation(organizationId, locationId)
      locationToDelete.value = null
      toastStore.success('Locatie succesvol verwijderd')
    } catch (error) {
      toastStore.error('Error deleting location:')
      throw error
    }
  }

  const showConfirmationDialog = computed({
    get: () => locationToDelete.value != null,
    set: value => {
      if (!value) locationToDelete.value = null
    },
  })

  const editLocation = ref<AddressBookLocation | null>(null)
  const originalEditLocation = ref<AddressBookLocation | null>(null)

  const editValidation = useServerValidation()
  const editAddressValidation = editValidation.scope('address', formatAddressErrors)
  editAddressValidation.clearOnChange(() => editLocation.value?.address)

  watch(
    () => editLocation.value?.locationNumber,
    () => editValidation.clear('locationNumber')
  )

  const showEditLocationModal = computed({
    get: () => editLocation.value !== null,
    set: value => {
      if (!value) closeEditModal()
    },
  })

  function openEditModal(location: AddressBookLocation) {
    const copy = structuredClone(toRaw(location))
    editLocation.value = copy
    originalEditLocation.value = structuredClone(copy)
  }

  function closeEditModal() {
    editLocation.value = null
    originalEditLocation.value = null
  }

  const selectedEditLocationTypes = computed<LocationTypeOption[]>({
    get: () =>
      locationTypeOptions.filter(option => editLocation.value?.types.includes(option.value)),
    set: items => {
      if (!editLocation.value) return
      const selected = new Set(items.map(item => item.value))
      editLocation.value.types = locationTypeOptions
        .filter(option => selected.has(option.value))
        .map(option => option.value)
    },
  })

  function toggleEditLocationType(item: LocationTypeOption) {
    const isSelected = selectedEditLocationTypes.value.some(type => type.value === item.value)
    selectedEditLocationTypes.value = isSelected
      ? selectedEditLocationTypes.value.filter(type => type.value !== item.value)
      : [...selectedEditLocationTypes.value, item]
  }

  const canSaveEditLocation = computed(
    () =>
      !!editLocation.value &&
      !!originalEditLocation.value &&
      !deepEqual(editLocation.value, originalEditLocation.value)
  )

  async function saveEditedLocation() {
    if (!organisationId.value || !editLocation.value) return

    const payload = normalizeEditedLocation(toRaw(editLocation.value))

    try {
      await addressBookStore.updateAddressBookLocation(
        organisationId.value,
        editLocation.value.id,
        payload
      )
      await addressBookStore.fetchAddressBookLocations(organisationId.value)

      closeEditModal()
      toastStore.success('Locatie succesvol geupdate')
    } catch (error) {
      const handled =
        isAxiosError(error) &&
        error.response?.status === 400 &&
        editValidation.setFromResponse(error.response.data)
      if (!handled) throw error
    }
  }
</script>

<template>
  <TopBar />
  <Modal v-model:show="showAddressBookLocationModal">
    <template #header>
      <h3>Nieuwe locatie</h3>
    </template>

    <FormInput
      v-model="newLocation.locationNumber"
      label="Locatienummer"
      :error="locationValidation.errors.value['locationNumber']"
    />

    <DropDown
      label="Locatie type"
      idKey="value"
      :allowNew="false"
      :allowMultiselect="true"
      v-model:multiselectData="selectedLocationTypes"
      :data="locationTypeOptions"
    >
      <template #empty>
        <div class="item">Selecteer locatie type(s)</div>
      </template>
      <template #multipleItems="{ items }">
        <div class="item-container">
          <span v-for="chosenItem in items" :key="chosenItem?.value" class="item">
            {{ chosenItem?.label }}
          </span>
        </div>
      </template>
      <template #item="{ item }">
        <div
          class="selection-option"
          :class="
            selectedLocationTypes.some(type => type.value === item?.value) && 'selected-option'
          "
          @click.stop="toggleLocationType(item)"
        >
          <span class="item-name">{{ item?.label }}</span>
        </div>
      </template>
    </DropDown>

    <AddressComponent
      v-if="newLocation.address"
      v-model="newLocation.address"
      :errors="locationAddressValidation.errors.value"
    />

    <template #footer="{ close }">
      <UiButton label="Annuleren" @click="close" />
      <UiButton
        label="Opslaan"
        variant="prominent"
        :disabled="!canSaveLocation"
        @click="saveNewLocation"
      />
    </template>
  </Modal>

  <Modal v-model:show="showEditLocationModal">
    <template #header>
      <h3>Locatie bewerken</h3>
    </template>

    <template v-if="editLocation">
      <FormInput
        v-model="editLocation.locationNumber"
        label="Locatienummer"
        :error="editValidation.errors.value['locationNumber']"
      />

      <DropDown
        label="Locatie type"
        idKey="value"
        :allowNew="false"
        :allowMultiselect="true"
        v-model:multiselectData="selectedEditLocationTypes"
        :data="locationTypeOptions"
      >
        <template #empty>
          <div class="item">Selecteer locatie type(s)</div>
        </template>
        <template #multipleItems="{ items }">
          <div class="item-container">
            <span v-for="chosenItem in items" :key="chosenItem?.value" class="item">
              {{ chosenItem?.label }}
            </span>
          </div>
        </template>
        <template #item="{ item }">
          <div
            class="selection-option"
            :class="
              selectedEditLocationTypes.some(type => type.value === item?.value) &&
              'selected-option'
            "
            @click.stop="toggleEditLocationType(item)"
          >
            <span class="item-name">{{ item?.label }}</span>
          </div>
        </template>
      </DropDown>

      <AddressComponent
        v-if="editLocation.address"
        v-model="editLocation.address"
        :errors="editAddressValidation.errors.value"
      />
    </template>

    <template #footer="{ close }">
      <UiButton label="Annuleren" @click="close" />
      <UiButton
        label="Opslaan"
        variant="prominent"
        :disabled="!canSaveEditLocation"
        @click="saveEditedLocation"
      />
    </template>
  </Modal>

  <div class="organisation-container">
    <div v-if="localAddressBookOrganisation" class="organisation-details">
      <div>
        <FormInput
          v-model="localAddressBookOrganisation.name"
          label="Naam"
          :error="validation.errors.value['name']"
        />
        <FormInput v-model="localAddressBookOrganisation.vihbNumber" label="VIHB nummer" />
        <DropDown
          label="Organisatie type"
          idKey="value"
          :allowNew="false"
          :allowMultiselect="true"
          v-model:multiselectData="selectedTypes"
          :data="organizationTypeOptions"
        >
          <template #empty>
            <div class="item">Selecteer organisatie type(s)</div>
          </template>
          <template #multipleItems="{ items }">
            <div class="item-container">
              <span v-for="chosenItem in items" :key="chosenItem?.value" class="item">
                {{ chosenItem?.label }}
              </span>
            </div>
          </template>
          <template #item="{ item }">
            <div
              class="selection-option"
              :class="selectedTypes.some(type => type.value === item?.value) && 'selected-option'"
              @click.stop="
                selectedTypes = selectedTypes.some(type => type.value === item?.value)
                  ? selectedTypes.filter(type => type.value !== item?.value)
                  : [...selectedTypes, item]
              "
            >
              <span class="item-name">{{ item?.label }}</span>
            </div>
          </template>
        </DropDown>
      </div>

      <div>
        <AddressComponent
          v-if="localAddressBookOrganisation.address"
          v-model="localAddressBookOrganisation.address"
          :errors="addressValidation.errors.value"
        />
      </div>
    </div>

    <div class="alignment">
      <UiButton label="Opslaan" variant="prominent" :disabled="!hasChanges" @click="saveChanges" />
    </div>

    <div>
      <Table
        :rows="addressBookStore.addressBookLocations"
        :columns="locationColumns"
        :configurable="true"
        configuration-storage-key="AddressBookDetailsTableConfig"
        row-key="id"
      >
        <template #table-top-actions>
          <UiButton
            label="Nieuwe locatie"
            variant="prominent"
            icon="plus"
            @click="openLocationModal"
          />
        </template>

        <template #cell-address="{ row }">
          {{ formatAddressForTable(row.address) }}
        </template>

        <template #cell-types="{ row }">
          <div v-if="row.types && row.types.length > 0">
            <span v-for="(type, index) in row.types" :key="index">
              {{ type }}<span v-if="index < row.types.length - 1">, </span>
            </span>
          </div>
        </template>

        <template #cell-action="{ row }">
          <div class="action-row">
            <UiButton
              label="Details"
              variant="prominent"
              icon="chevronRight"
              @click="openEditModal(row)"
            />

            <UiButton variant="negative" @click="openDeleteDialog(row)" icon="delete" />
          </div>
        </template>
      </Table>
    </div>
  </div>

  <ConfirmationDialog
    :title="`Are you sure you want to remove this location?`"
    body="This action cannot be undone."
    confirmButtonText="Remove"
    confirmButtonColor="negative"
    v-model:show="showConfirmationDialog"
    :confirm="() => locationToDelete && deleteLocation(locationToDelete.id!)"
  />
</template>

<style scoped lang="css">
  .organisation-container {
    padding: var(--spacing-s);
  }

  .alignment {
    justify-self: end;
  }

  .organisation-details {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 1rem;
  }

  .organisation-details > * {
    flex: 1;
  }

  .item-container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: start;
  }

  .item {
    padding: var(--spacing-xs);
  }

  .selection-option {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
  }

  .selection-option .item-name {
    flex: 1;
  }

  .selected-option {
    font-weight: bold;
  }

  .action-row {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-s);
  }
</style>
